// @ts-nocheck
"use client";

import React, { useEffect, useState, useRef } from "react";
import styles from "./search-modal.module.css";
import clsx from "clsx";
import { FaMagnifyingGlass } from "react-icons/fa6";
import { AiOutlineLoading } from "react-icons/ai";

const DEBOUNCE_DELAY = 800;

// Shape one smart-search result for the legacy fnCatch consumers (tabs.tsx, show-all-results.tsx).
// Consumers expect: { title, link, crumbs, matchedReferences, score, date, type }
function shapeResult(item) {
    const meta = item?.meta || {};
    const sectionTitle = meta?.section?.title || "";
    const typeTitle = meta?.type?.title || "";

    const crumbs = [sectionTitle, typeTitle].filter(Boolean).join(" > ");

    const industries = Array.isArray(meta?.industries) ? meta.industries : (meta?.industries ? [meta.industries] : []);
    const solutions = Array.isArray(meta?.solutions) ? meta.solutions : (meta?.solutions ? [meta.solutions] : []);

    // if (industries.length > 0) {
    //     console.log("[shapeResult] industries:", item?.title, industries);
    // }
    // if (solutions.length > 0) {
    //     console.log("[shapeResult] solutions:", item?.title, solutions);
    // }

    return {
        title: item?.title,
        link: item?.link || item?.path || "",
        crumbs,
        matchedReferences: item?.matchedReferences || [],
        score: item?.score ?? 0,
        date: item?.publishDate ?? null,
        // Forwarded meta for downstream dropdown filters (ShowAllResults)
        section: sectionTitle,
        typeTitle,
        topics: Array.isArray(meta?.topic) ? meta.topic : (meta?.topic ? [meta.topic] : []),
        industries,
        solutions,
        ...(typeTitle && { type: typeTitle }),
    };
}

function SearchModal({
    fnCatch,
    tabOpen = false,
    isMobile,
    inputValue = "",
    mode = "free",
    loading = false,
    setLoading,
}) {
    const [input, setInput] = useState(inputValue);
    const debounceTimeout = useRef(null);
    const inputRef = useRef(null);

    const runSearch = async (value) => {
        const query = (value || "").trim();
        if (!query) {
            fnCatch({ input: "", results: [], blogs: [] });
            return;
        }

        setLoading?.(true);
        try {
            const res = await fetch("/api/smart-search", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ query }),
            });
            const data = await res.json();
            // console.log("[smart-search] raw API response:", data);

            if (!res.ok || data?.error) {
                console.error("smart-search failed:", data?.error || res.status);
                fnCatch({ input: query, results: [], blogs: [] });
                return;
            }

            const results = (data?.results || []).map((r) => shapeResult(r));

            // Consumer (tabs.tsx) uses `blog.attributes` -> vxiContentInterpreter,
            // so resolve each smart-search blog hit into a full Strapi entry.
            const apiBlogs = data?.blogs || [];
            const blogs = (
                await Promise.all(
                    apiBlogs.map(async (b) => {
                        if (!b?.id || !b?.endpoint) return null;
                        try {
                            const r = await fetch(
                                `/api/result-blogs?id=${b.id}&endpoint=${encodeURIComponent(b.endpoint)}`
                            );
                            const j = await r.json();
                            return j?.data || null;
                        } catch (err) {
                            console.error("Failed to fetch blog:", err);
                            return null;
                        }
                    })
                )
            )
                .filter(Boolean)
                .slice(0, 3);

            fnCatch({ input: query, results, blogs });
            // console.log("[smart-search] shaped payload:", { input: query, results, blogs });
        } catch (err) {
            console.error("smart-search error:", err);
            fnCatch({ input: query, results: [], blogs: [] });
        } finally {
            setLoading?.(false);
        }
    };

    // Keep input in sync with parent-provided value and trigger a debounced search.
    useEffect(() => {
        if (!inputValue?.trim()) return;
        setInput(inputValue);
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        debounceTimeout.current = setTimeout(() => runSearch(inputValue), DEBOUNCE_DELAY);
    }, [inputValue]);

    const handleChange = (e) => {
        const value = e?.target?.value ?? "";
        setInput(value);

        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        debounceTimeout.current = setTimeout(() => runSearch(value), DEBOUNCE_DELAY);
    };

    return (
        <div
            className={clsx(
                styles["search-wrapper"],
                (input?.trim() !== "" || mode === "filter") && styles["active"],
                tabOpen && styles["tab-open"],
                isMobile && styles["mobile"]
            )}
            onMouseEnter={() => inputRef.current?.focus()}
        >
            {loading ? (
                <AiOutlineLoading size={18} className={styles.rotating} />
            ) : (
                <FaMagnifyingGlass size={18} />
            )}
            <input
                ref={inputRef}
                value={input}
                onChange={handleChange}
                placeholder="Search..."
            />
        </div>
    );
}

export default SearchModal;
